/* eslint-disable react-hooks/exhaustive-deps */
/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { Children, useEffect, useState } from "react";

// react-router-dom components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React context
import { setLayout, setMiniSidenav, useMaterialUIController } from "context";
import AppHeader from "components/AppHeader";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { appConstants } from "utils/appConstants";
import { InlineFormMessage } from "components/InlineFeedback/InlineFeedback";

function DashboardLayout({ children }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const { pathname } = useLocation();
  const [user, setUser] = useState(() => {
    try {
      return (
        JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "null") || {}
      );
    } catch {
      return {};
    }
  });

  useEffect(() => {
    const handleProfileUpdate = () => {
      try {
        setUser(
          JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "null") || {}
        );
      } catch {}
    };
    window.addEventListener("user-profile-updated", handleProfileUpdate);
    window.addEventListener("storage", handleProfileUpdate);
    return () => {
      window.removeEventListener("user-profile-updated", handleProfileUpdate);
      window.removeEventListener("storage", handleProfileUpdate);
    };
  }, []);

  const roleName =
    sessionStorage.getItem("ROLE_NAME") ||
    JSON.parse(sessionStorage.getItem("ROLE") || "{}")?.roleName;
  const isSuperAdmin =
    roleName === "Super Admin" || user?.userType?.toLowerCase() === "admin";

  useEffect(() => {
    setLayout(dispatch, "dashboard");
  }, [pathname]);

  useEffect(() => {
    const saved = sessionStorage.getItem("DESKTOP_SIDEBAR_COLLAPSED");
    if (saved === "true" && window.innerWidth > 1024) {
      setMiniSidenav(dispatch, true);
      document.body.classList.add("sidebar-collapsed");
    }
    const handleSidebarToggle = (event) => {
      const collapsed = event?.detail?.collapsed;
      if (typeof collapsed === "boolean") {
        setMiniSidenav(dispatch, collapsed);
      }
    };
    window.addEventListener("desktop-sidebar-toggled", handleSidebarToggle);
    return () =>
      window.removeEventListener(
        "desktop-sidebar-toggled",
        handleSidebarToggle
      );
  }, [dispatch]);

  if (isSuperAdmin) {
    return (
      <MDBox
        sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
          px: 3,
          py: 1,
          position: "relative",

          [breakpoints.up("xl")]: {
            marginLeft: miniSidenav ? pxToRem(120) : pxToRem(274),
            transition: transitions.create(["margin-left", "margin-right"], {
              easing: transitions.easing.easeInOut,
              duration: transitions.duration.standard,
            }),
          },
        })}
      >
        <InlineFormMessage scope="global" />
        {children}
      </MDBox>
    );
  }

  const userName =
    user.fullName ||
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    user.name ||
    user.companyName ||
    user.businessName ||
    user.organisationName ||
    user.organizationName ||
    user.emailAddress ||
    user.email ||
    "User";
  const rawPic =
    user.profilePictureFileName ||
    user.profilePicFileName ||
    user.profileImage ||
    user.avatar ||
    user.photo;
  const profileImage = (() => {
    if (!rawPic || typeof rawPic !== "string" || !rawPic.trim()) return "";
    const trimmed = rawPic.trim();
    if (trimmed === "null" || trimmed === "undefined" || trimmed === "empty")
      return "";
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("data:") ||
      trimmed.startsWith("blob:")
    ) {
      return trimmed;
    }
    return `${appConstants.baseURL}user/viewFile?fileName=${encodeURIComponent(
      trimmed
    )}`;
  })();
  const content = Children.toArray(children).filter(
    (child) => child?.type !== DashboardNavbar
  );

  return (
    <MDBox
      className="global-dashboard-layout"
      sx={({ breakpoints, functions: { pxToRem } }) => ({
        p: 0,
        position: "relative",
        transition: "margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        [breakpoints.up("lg")]: {
          marginLeft: miniSidenav ? 0 : pxToRem(256),
        },
      })}
    >
      <AppHeader
        onOpenNav={() =>
          window.dispatchEvent(new CustomEvent("open-dashboard-navigation"))
        }
        userName={userName}
        profileImage={profileImage}
      />
      <MDBox sx={{ px: 3, py: 1, width: "100%" }}>
        <InlineFormMessage scope="global" />
        {content}
      </MDBox>
    </MDBox>
  );
}

// Typechecking props for the DashboardLayout
DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DashboardLayout;
